import Vue from "vue";
import Router from "vue-router";
import store from "@/store";
import { readCookie, eraseCookie, createCookie, $http } from "@/assets/js/appFunc";
import { PLATFORM_BASE_PATH } from "@/assets/js/appConfig";

Vue.use(Router);

const downloadCases = [
    { condition: "downloadCsv?fileName",
        value: `/${PLATFORM_BASE_PATH}/export?fileName=` },
    { condition: "downloadGDM?fileName",
        value: `/${PLATFORM_BASE_PATH}/export?type=GDM&fileName=` },
    { condition: "downloadAuditCSV?fileName",
        value: `/${PLATFORM_BASE_PATH}/export?type=LOG&fileName=` },
    { condition: "dsReport?fileName",
        value: `/${PLATFORM_BASE_PATH}/export?type=DS&fileName=` },
    { condition: "dsExport?fileName",
        value: `/${PLATFORM_BASE_PATH}/export?type=DS_EXPORT&fileName=` }
];

function removeExternalToken() {
    if (localStorage.getItem("externalToken")) {
        localStorage.removeItem("externalToken");
    }
}
function addDownloadInitialUrl(to) {
    const downloadType = downloadCases.find(({ condition }) => to.fullPath.includes(condition) || to.redirectedFrom?.includes(condition));
    if (downloadType) {
        createCookie("initialURL", encodeURIComponent(window.location.href), 1 / 24);
    }
}

function clearCookie(next) {
    eraseCookie("jwt1");
    eraseCookie("jwt2");
    removeExternalToken();
    next(true);
}

function logoutCurrentUser(next) {
    sessionStorage.clear();
    localStorage.clear();
    $http({ url: "/auth/logout", type: "GET" }, true).then(
        () => {
            localStorage.setItem("fromSAML", false);
            clearCookie(next);
        }
    );
}

function checkDownloadType(to) {
    const downloadType = downloadCases.find(({ condition }) => to.fullPath.includes(condition) || to.redirectedFrom?.includes(condition));
    return downloadType ? `${downloadType.value}${to.query.fileName}` : `/${PLATFORM_BASE_PATH}/`;
}

function startApp(pid) {
    $http({
        url: `/engine/api/processDefinition/${pid}/start`,
        type: "GET",
        noNotify: true
    }, true).then(({ data: dataRsp }) => {
        const data = JSON.parse(JSON.stringify(dataRsp));
        if (data.guiId && data.taskId) {
            localStorage.removeItem("ifPid");
            store().dispatch("components/claimTask", data.taskId, { root: true }).then(rsp => {
                data.canUnclaim = rsp.data.canUnclaim;
                data.__dashboard = true;
                window.location.assign(`/${PLATFORM_BASE_PATH}/tasks/${data.taskId}`);
            });
        } else {
            sessionStorage.setItem("triggerNoTaskPermission", "noPermisionsToStartApp");
            window.location.assign(`/${PLATFORM_BASE_PATH}`);
        }
    })
        .catch(() => {
            sessionStorage.setItem("triggerNoTaskPermission", "noPermisionsToInitiateApp");
            window.location.assign(`/${PLATFORM_BASE_PATH}`);
        });
}

const router = new Router({
    mode: "history",
    routes: [
        {
            // identity/user-information/
            path: "/login",
            name: "login",
            alias: [
                "/maintenance",
                "/maintenance/login"
            ],
            component: () => import(/* webpackChunkName: "loginPage" */ "./views/Login/Login.vue"),
            beforeEnter: (to, from, next) => {
                const jwt1 = readCookie("jwt1");
                const wrongTenant = JSON.parse(localStorage.getItem("wrongTenantActive"));
                if (!wrongTenant) {
                    if (jwt1) {
                        store()
                            .dispatch("main/checkJWT")
                            .then(
                                res => {
                                    const { data: { userType } = {} } = res;
                                    if (userType) {
                                        addDownloadInitialUrl(to);
                                        localStorage.setItem("isUserUsr", userType === "TUSR");
                                        clearCookie(next);
                                    } else {
                                        const externalToken = localStorage.getItem("externalToken");
                                        if (externalToken) {
                                            localStorage.removeItem("externalToken");
                                            logoutCurrentUser(next);
                                        } else {
                                            window.location.assign(checkDownloadType(to));
                                        }
                                    }
                                }
                            )
                            .catch(() => {
                                removeExternalToken();
                                addDownloadInitialUrl(to);
                                next(true);
                            });
                    } else {
                        store().dispatch("main/actMaintenanceActive").then(rsp => {
                            if (rsp.data.inProgress) {
                                localStorage.setItem("maintenanceStarted", rsp.data.inProgress);
                                if (rsp.data.redirectUrl && !window.location.href.includes("maintenance")) {
                                    window.location.href = rsp.data.redirectUrl;
                                }
                            } else {
                                localStorage.removeItem("maintenanceStarted");
                                localStorage.removeItem("maintenanceUser");
                                if (to.name === "login" && (to.path === "/maintenance" || to.path === "/maintenance/login")) {
                                    window.location.href = "/login";
                                }
                            }
                        });
                        removeExternalToken();
                        addDownloadInitialUrl(to);
                        next(true);
                    }
                }
            }
        },
        {
            path: "*",
            redirect: "/login"
        },
        {
            path: "/startApp",
            name: "start",
            component: () => import(/* webpackChunkName: "loginPage" */ "./views/Login/Login.vue"),
            props: (route) => {
                const { processDefinitionId = "", appCode = "" } = route.query;
                const pid = processDefinitionId || appCode || null;

                return { pid };
            },
            beforeEnter: (to, from, next) => {
                const jwt1 = readCookie("jwt1");
                const { processDefinitionId = "", appCode = "" } = to.query;
                const pid = processDefinitionId || appCode || null;
                if (jwt1 && pid) {
                    store()
                        .dispatch("main/checkJWT")
                        .then(
                            res => {
                                const { data: { userType } = {} } = res;
                                if (userType) {
                                    localStorage.setItem("isUserUsr", userType === "TUSR");
                                    clearCookie(next);
                                } else {
                                    startApp(pid);
                                }
                            }
                        )
                        .catch(() => {
                            next(true);
                        });
                } else {
                    next(true);
                }
            }
        },
        {
            path: "/reset",
            name: "reset",
            component: () => import(/* webpackChunkName: "reset" */ "./views/Reset/Reset.vue")
        },
        {
            path: "/invite",
            name: "invite",
            component: () => import(/* webpackChunkName: "invite" */ "./views/Invite/Invite.vue"),
            beforeEnter: (to, from, next) => {
                if (to.query && to.query.userId) {
                    localStorage.setItem("createUserId", to.query.userId);
                    next({ path: "/login" });
                }
                next(true);
            }
        },
        {
            path: "/changeWallet",
            name: "changeWalletPassword",
            component: () => import("./views/Login/ChangePassword.vue")
        },

        {
            path: "/twoFactor",
            name: "twoFactor",
            component: () => import("@/components/Login/accessCodeFactorAuth.vue")
        },
        {
            path: "/phoneNumber",
            name: "phoneNumber",
            component: () => import("@/components/Login/securePhoneNr.vue")
        },
        {
            path: "/password-successfully-reset",
            name: "passReset",
            component: () => import("@/components/Login/passResetCnfScreen.vue")
        },
        // used on sso
        {
            path: "/failedLogin",
            name: "accountDisabled",
            component: () => import("@/components/Login/accountDisabled.vue")
        }
    ]
});

export default router;
